import {
	getBaseNodeTypeName,
	isNodeOfSameBaseType,
} from '@atlaskit/editor-common/utils/node-type-utils';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Fragment } from '@atlaskit/editor-prosemirror/model';

type RequestedNode = {
	attrs?: Record<string, unknown>;
	content?: RequestedNode[];
	type: string;
};

type FirstStructureDifference = {
	actualNodeType: string;
	expectedNodeType: string;
	firstDifferenceAttribute?: string;
	firstDifferenceKind: 'attribute' | 'missing_node' | 'node_type';
	firstDifferencePath: string;
};

function sameJsonValue(expected: unknown, actual: unknown): boolean {
	if (expected === actual) {
		return true;
	}
	if (
		expected === null ||
		actual === null ||
		typeof expected !== 'object' ||
		typeof actual !== 'object'
	) {
		return false;
	}
	if (Array.isArray(expected) || Array.isArray(actual)) {
		return (
			Array.isArray(expected) &&
			Array.isArray(actual) &&
			expected.length === actual.length &&
			expected.every((value, index) => sameJsonValue(value, actual[index]))
		);
	}
	const expectedKeys = Object.keys(expected);
	const actualRecord = actual as Record<string, unknown>;
	return (
		expectedKeys.length === Object.keys(actualRecord).length &&
		expectedKeys.every(
			(key) =>
				Object.prototype.hasOwnProperty.call(actualRecord, key) &&
				sameJsonValue((expected as Record<string, unknown>)[key], actualRecord[key]),
		)
	);
}

function nonTextChildren(node: PMNode): PMNode[] {
	const children: PMNode[] = [];
	node.forEach((child) => {
		// Adjacent text runs can legitimately merge during application.
		if (!child.isText) {
			children.push(child);
		}
	});
	return children;
}

function requestedNonTextChildren(node: RequestedNode | undefined): RequestedNode[] {
	return node?.content?.filter((child) => child.type !== 'text') ?? [];
}

function hasUnsupportedRequestedAttributes(node: PMNode, requested: RequestedNode): boolean {
	if (
		Object.keys(requested.attrs ?? {}).some(
			(key) =>
				key !== 'localId' && !Object.prototype.hasOwnProperty.call(node.type.spec.attrs ?? {}, key),
		)
	) {
		return true;
	}
	const children = nonTextChildren(node);
	const requestedChildren = requestedNonTextChildren(requested);
	return children.some((child, index) =>
		hasUnsupportedRequestedAttributes(child, requestedChildren[index]),
	);
}

function firstStructureDifference(
	expected: PMNode | undefined,
	actual: PMNode | undefined,
	path: number[],
	requested?: RequestedNode,
): FirstStructureDifference | undefined {
	const expectedNodeType = expected ? getBaseNodeTypeName(expected.type) : 'missing';
	const actualNodeType = actual ? getBaseNodeTypeName(actual.type) : 'missing';
	const firstDifferencePath = path.join('.');
	if (!expected || !actual) {
		return {
			expectedNodeType,
			actualNodeType,
			firstDifferencePath,
			firstDifferenceKind: 'missing_node',
		};
	}
	if (!isNodeOfSameBaseType(expected.type, actual.type)) {
		return {
			expectedNodeType,
			actualNodeType,
			firstDifferencePath,
			firstDifferenceKind: 'node_type',
		};
	}
	// Check only keys present in the raw model response. ProseMirror fills omitted
	// attrs with schema defaults, and the Editor may add more during application.
	for (const [key, value] of Object.entries(requested?.attrs ?? {})) {
		// The Editor assigns fresh local IDs during replacement.
		if (key !== 'localId' && !sameJsonValue(value, actual.attrs[key])) {
			return {
				expectedNodeType,
				actualNodeType,
				firstDifferencePath,
				firstDifferenceKind: 'attribute',
				firstDifferenceAttribute: key,
			};
		}
	}
	const expectedChildren = nonTextChildren(expected);
	const actualChildren = nonTextChildren(actual);
	const requestedChildren = requestedNonTextChildren(requested);
	for (let index = 0; index < Math.max(expectedChildren.length, actualChildren.length); index++) {
		const difference = firstStructureDifference(
			expectedChildren[index],
			actualChildren[index],
			[...path, index],
			requestedChildren[index],
		);
		if (difference) {
			return difference;
		}
	}
	return undefined;
}

/** A bounded node-type tree. Never send text or attribute values to analytics. */
function summarizeNodeTypes(nodes: (PMNode | undefined)[]): {
	summary: string;
	truncated: boolean;
} {
	const maxNodes = 32;
	const maxDepth = 8;
	let count = 0;
	let truncated = false;
	const visit = (node: PMNode, depth: number): string => {
		if (count >= maxNodes || depth >= maxDepth) {
			truncated = true;
			return '…';
		}
		count++;
		const children: string[] = [];
		let omittedChildren = false;
		node.forEach((child) => {
			if (child.isText || omittedChildren) {
				return;
			}
			if (count >= maxNodes || depth + 1 >= maxDepth) {
				truncated = true;
				children.push('…');
				omittedChildren = true;
				return;
			}
			children.push(visit(child, depth + 1));
		});
		return children.length ? `${node.type.name}(${children.join(',')})` : node.type.name;
	};
	const summaries: string[] = [];
	for (const node of nodes) {
		if (count >= maxNodes) {
			truncated = true;
			summaries.push('…');
			break;
		}
		if (node) {
			summaries.push(visit(node, 0));
		} else {
			count++;
			summaries.push('missing');
		}
	}
	return {
		summary: summaries.join(','),
		truncated,
	};
}

export type ReplacementStructureComparison =
	| { mismatch: false }
	| {
			actualNodeType: string;
			actualStructure: string;
			comparisonScope: 'ancestor' | 'replacement';
			expectedNodeType: string;
			expectedStructure: string;
			firstDifferenceAttribute?: string;
			firstDifferenceKind: 'attribute' | 'missing_node' | 'node_type';
			firstDifferencePath: string;
			mismatch: true;
			structureTruncated: boolean;
	  };

function mismatchDetails(
	expected: PMNode[],
	actual: (PMNode | undefined)[],
	comparisonScope: 'ancestor' | 'replacement',
	requested?: RequestedNode[],
): ReplacementStructureComparison {
	const expectedSummary = summarizeNodeTypes(expected);
	const actualSummary = summarizeNodeTypes(actual);
	let firstDifference: FirstStructureDifference | undefined;
	for (let index = 0; index < expected.length; index++) {
		firstDifference = firstStructureDifference(
			expected[index],
			actual[index],
			[index],
			requested?.[index],
		);
		if (firstDifference) {
			break;
		}
	}
	if (!firstDifference) {
		throw new Error('Structural mismatch has no difference');
	}
	return {
		mismatch: true,
		...firstDifference,
		expectedStructure: expectedSummary.summary,
		actualStructure: actualSummary.summary || 'missing',
		structureTruncated: expectedSummary.truncated || actualSummary.truncated,
		comparisonScope,
	};
}

/**
 * `undefined` means the request cannot safely be verified (partial/invalid ADF,
 * unresolved target, or replacement requiring schema fitting). Never blame the apply
 * layer for those cases. The raw final chunk is used before streamer transforms can
 * flatten requested wrappers. Only JSON ADF replaceNode responses are verified here.
 */
export function compareReplacementStructure(
	before: PMNode,
	after: PMNode,
	targetLocalId: unknown,
	response: string,
): ReplacementStructureComparison | undefined {
	if (typeof targetLocalId !== 'string' || targetLocalId.length === 0) {
		return undefined;
	}
	try {
		const requested = JSON.parse(response);
		if (
			requested?.type !== 'doc' ||
			!Array.isArray(requested.content) ||
			!requested.content.length
		) {
			return undefined;
		}
		const replacement = Fragment.fromArray(
			requested.content.map((json: unknown) => before.type.schema.nodeFromJSON(json)),
		);
		replacement.forEach((node) => node.check());
		const requestedNodes = requested.content as RequestedNode[];
		// Unknown attributes are an unverifiable model request, not an apply failure.
		let unsupportedAttributes = false;
		replacement.forEach((node, _offset, index) => {
			unsupportedAttributes ||= hasUnsupportedRequestedAttributes(node, requestedNodes[index]);
		});
		if (unsupportedAttributes) {
			return undefined;
		}
		let targetPos: number | undefined;
		// Stop at the second match: duplicate IDs make the applied region ambiguous.
		const findTarget = (parent: PMNode, start: number): boolean => {
			let pos = start;
			for (let index = 0; index < parent.childCount; index++) {
				const node = parent.child(index);
				if (node.attrs.localId === targetLocalId) {
					if (targetPos !== undefined) {
						return false;
					}
					targetPos = pos;
				}
				if (!findTarget(node, pos + 1)) {
					return false;
				}
				pos += node.nodeSize;
			}
			return true;
		};
		if (!findTarget(before, 0) || targetPos === undefined) {
			return undefined;
		}
		const resolved = before.resolve(targetPos);
		// List replacements can legitimately unwrap or lift out of the target's
		// ancestors during slice fitting. The queue may already receive a synthetic
		// task/decision-list wrapper, so these are not safe structural comparisons.
		const listTypes = new Set(['bulletList', 'orderedList', 'taskList', 'decisionList']);
		let replacesWithList = false;
		replacement.forEach((node) => {
			replacesWithList ||= listTypes.has(node.type.name);
		});
		if (replacesWithList) {
			for (let depth = resolved.depth; depth > 0; depth--) {
				if (listTypes.has(resolved.node(depth).type.name)) {
					return undefined;
				}
			}
		}
		const index = resolved.index();
		if (!resolved.parent.canReplace(index, index + 1, replacement)) {
			return undefined;
		}
		// Follow the original target's ancestry, not its localId: replacements may
		// regenerate every ID. Comparing only this region avoids unrelated panels
		// elsewhere in the document masking a dropped wrapper.
		let parent = after;
		for (let depth = 0; depth < resolved.depth; depth++) {
			const child = parent.maybeChild(resolved.index(depth));
			if (!child || !isNodeOfSameBaseType(child.type, resolved.node(depth + 1).type)) {
				return mismatchDetails([resolved.node(depth + 1)], child ? [child] : [], 'ancestor');
			}
			parent = child;
		}
		const expectedNodes: PMNode[] = [];
		const actualNodes: (PMNode | undefined)[] = [];
		let mismatch = false;
		replacement.forEach((node, _offset, childIndex) => {
			const applied = parent.maybeChild(index + childIndex);
			expectedNodes.push(node);
			actualNodes.push(applied ?? undefined);
			if (
				firstStructureDifference(
					node,
					applied ?? undefined,
					[childIndex],
					requestedNodes[childIndex],
				)
			) {
				mismatch = true;
			}
		});
		return mismatch
			? mismatchDetails(expectedNodes, actualNodes, 'replacement', requestedNodes)
			: { mismatch: false };
	} catch {
		return undefined;
	}
}
